import { Fragment, useEffect, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link, Navigate } from 'react-router-dom';
import {
  useContentSubmissionsQuery,
  useContentSubmissionDetailQuery,
  useContentPendingCountQuery,
} from '../api/queries';
import { contentApi } from '../api/content';
import { useAuth } from '../hooks/useAuth';
import { showToast } from '../components/Toast';
import { fireConfetti } from '../lib/confetti';
import { renderEmbedsForPreview } from '../lib/embeds';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
// The same editor and blots the franchisee wrote the post in — see quillConfig.
import {
  QUILL_MODULES,
  QUILL_FORMATS,
  QUILL_MODULES_BLOG,
  QUILL_FORMATS_BLOG,
  isQuillBlank,
} from '../lib/quillConfig';
import { normalizeHeadings } from '../lib/contentHtml';
import type {
  AdminContentSubmissionRow,
  ContentSubmissionStatus,
} from '../types';

const STATUS_TABS: Array<{ id: ContentSubmissionStatus | 'all'; label: string }> = [
  { id: 'pending', label: 'Pending' },
  { id: 'approved', label: 'Approved' },
  { id: 'published', label: 'Published' },
  { id: 'rejected', label: 'Rejected' },
  { id: 'publish_failed', label: 'Publish Failed' },
  { id: 'all', label: 'All' },
];

const STATUS_COLOR: Record<ContentSubmissionStatus, string> = {
  pending: '#f59e0b',
  approved: '#3b82f6',
  rejected: '#dc2626',
  published: '#16a34a',
  publish_failed: '#dc2626',
};

// Preset rejection reasons shown to the reviewer. Picking "Other" requires a
// written note. Whatever is chosen is emailed to the franchisee.
const REJECT_REASONS = [
  'Too generic',
  'Not related to business',
  'Needed significant editing',
  'Other',
];

function fmt(s: string | null): string {
  if (!s) return '';
  try {
    return new Date(s).toLocaleString();
  } catch {
    return s;
  }
}

function rowHeadline(r: AdminContentSubmissionRow): string {
  if (r.submission_type === 'news_events') return r.news_headline || '(News & Events)';
  if (r.submission_type === 'blog') return r.blog_headline || '(Blog Post)';
  return [r.news_headline, r.blog_headline].filter(Boolean).join(' / ') || '(News + Blog)';
}

// Held pending rows read "Hold by Jon" in purple instead of "pending".
const HOLD_COLOR = '#7c3aed';

function StatusPill({ status, heldBy }: { status: ContentSubmissionStatus; heldBy?: string | null }) {
  const held = status === 'pending' && !!heldBy;
  const color = held ? HOLD_COLOR : STATUS_COLOR[status];
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: 12,
        background: color + '22',
        color,
        fontWeight: 600,
        fontSize: 12,
        whiteSpace: 'nowrap',
      }}
    >
      {held
        ? `Hold by ${heldBy}`
        : status === 'approved'
          ? 'Approved, working on upload.'
          : status.replace('_', ' ')}
    </span>
  );
}

// ---------------------------------------------------------------------------
// DismissFailureButton — takes a failed publish off the queue without deleting
// it. The row keeps its content and error and still shows under All; it just
// stops counting against the badge and stops sitting in the work queue.
//
// For failures nobody intends to retry — old test rows, a post the franchisee
// has since withdrawn — which otherwise flag the tab forever.
// ---------------------------------------------------------------------------
function DismissFailureButton(props: {
  submissionId: number;
  onDone: () => Promise<void> | void;
  variant: 'row' | 'drawer';
}) {
  const [busy, setBusy] = useState(false);

  async function dismiss(e: React.MouseEvent) {
    e.stopPropagation();
    const ok = window.confirm(
      'Remove this from the Publish Failed queue?\n\n' +
        'The submission is NOT deleted — it keeps its content and error and ' +
        'stays visible under the All tab. It just stops flagging this tab.\n\n' +
        'Hitting Retry publish later brings it back.',
    );
    if (!ok) return;
    setBusy(true);
    try {
      await contentApi.dismissFailure(props.submissionId);
      showToast('Removed from the queue. Still visible under All.', {
        variant: 'success',
      });
      await props.onDone();
    } catch (err) {
      showToast(`Could not remove: ${(err as Error).message}`, { variant: 'error' });
    } finally {
      setBusy(false);
    }
  }

  const drawer = props.variant === 'drawer';
  return (
    <button
      type="button"
      onClick={dismiss}
      disabled={busy}
      style={{
        padding: drawer ? '10px 20px' : '4px 10px',
        background: '#fff',
        color: '#475569',
        border: '1px solid #cbd5e1',
        borderRadius: 4,
        fontSize: drawer ? 14 : 13,
        fontWeight: 600,
        cursor: busy ? 'not-allowed' : 'pointer',
        opacity: busy ? 0.6 : 1,
        whiteSpace: 'nowrap',
      }}
    >
      {busy ? 'Removing…' : 'Remove'}
    </button>
  );
}

// ---------------------------------------------------------------------------
// RetryPublishButton — puts a failed publish back on the publisher's queue.
//
// The publisher polls every minute, so the post goes out on its next pass;
// nothing here talks to WordPress directly.
//
// The confirm is not ceremony. A failure late in the blog flow can happen
// AFTER the WordPress post was created, and the publisher records nothing
// about a partial post when it fails — so a retry in that case makes a second
// copy. The marketer is the one who can check the site and tell.
// ---------------------------------------------------------------------------
function RetryPublishButton(props: {
  submissionId: number;
  onDone: () => Promise<void> | void;
  variant: 'row' | 'drawer';
}) {
  const [busy, setBusy] = useState(false);

  async function retry(e: React.MouseEvent) {
    e.stopPropagation();
    const ok = window.confirm(
      'Retry publishing this post?\n\n' +
        'It goes back in the publish queue and should go out within a minute.\n\n' +
        'If the failed attempt already created the post in WordPress, retrying ' +
        'can create a second copy — check the site first if you are not sure.',
    );
    if (!ok) return;
    setBusy(true);
    try {
      await contentApi.retryPublish(props.submissionId);
      showToast('Back in the publish queue — should go out within a minute.', {
        variant: 'success',
      });
      await props.onDone();
    } catch (err) {
      showToast(`Retry failed: ${(err as Error).message}`, { variant: 'error' });
    } finally {
      setBusy(false);
    }
  }

  const drawer = props.variant === 'drawer';
  return (
    <button
      type="button"
      onClick={retry}
      disabled={busy}
      style={{
        padding: drawer ? '10px 20px' : '4px 10px',
        background: '#dc2626',
        color: 'white',
        border: 'none',
        borderRadius: 4,
        fontSize: drawer ? 14 : 13,
        fontWeight: 600,
        cursor: busy ? 'not-allowed' : 'pointer',
        opacity: busy ? 0.6 : 1,
        whiteSpace: 'nowrap',
      }}
    >
      {busy ? 'Retrying…' : 'Retry publish'}
    </button>
  );
}

export default function ContentApprovalsPage() {
  const { user, isLoading } = useAuth();
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<ContentSubmissionStatus | 'all'>('pending');
  const [openId, setOpenId] = useState<number | null>(null);

  const list = useContentSubmissionsQuery(statusFilter, !!user && user.role === 'admin');
  // Drives the Publish Failed tab badge and the banner. Polls on the same
  // 30s interval as the navbar bell, so a fresh failure surfaces on its own
  // without the marketer reloading the page.
  const counts = useContentPendingCountQuery(!!user && user.role === 'admin');
  const failedCount = counts.data?.failedCount ?? 0;

  async function refreshAll() {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['content-submissions'] }),
      queryClient.invalidateQueries({ queryKey: ['content-pending-count'] }),
    ]);
  }

  if (isLoading) return <div className="auth-loading">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin') {
    return (
      <div style={{ padding: 32, textAlign: 'center' }}>
        <h2>Not authorized</h2>
        <p>This page is for admin users only.</p>
        <Link to="/">Back to dashboard</Link>
      </div>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, marginBottom: 16 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Content Approvals</h1>
        <Link to="/" style={{ fontSize: 14, color: '#3b82f6' }}>← Back to dashboard</Link>
      </div>

      {failedCount > 0 && statusFilter !== 'publish_failed' && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            marginBottom: 16,
            padding: '10px 14px',
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: 6,
            color: '#991b1b',
            fontSize: 14,
          }}
        >
          <span style={{ fontSize: 16 }}>⚠️</span>
          <span>
            <strong>
              {failedCount} post{failedCount === 1 ? '' : 's'} failed to publish
            </strong>{' '}
            and {failedCount === 1 ? 'is' : 'are'} not live. Most failures are
            temporary — open the tab and hit Retry.
          </span>
          <button
            type="button"
            onClick={() => setStatusFilter('publish_failed')}
            style={{
              marginLeft: 'auto',
              padding: '6px 12px',
              background: '#dc2626',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            Review {failedCount === 1 ? 'it' : 'them'}
          </button>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, borderBottom: '1px solid #e2e8f0' }}>
        {STATUS_TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setStatusFilter(t.id)}
            style={{
              padding: '8px 14px',
              border: 'none',
              background: 'transparent',
              borderBottom:
                statusFilter === t.id ? '2px solid #3b82f6' : '2px solid transparent',
              color: statusFilter === t.id ? '#1e293b' : '#64748b',
              fontWeight: statusFilter === t.id ? 600 : 400,
              cursor: 'pointer',
            }}
          >
            {t.label}
            {t.id === 'publish_failed' && failedCount > 0 && (
              <span
                style={{
                  marginLeft: 6,
                  background: '#dc2626',
                  color: 'white',
                  fontSize: 11,
                  fontWeight: 700,
                  padding: '1px 7px',
                  borderRadius: 9,
                }}
              >
                {failedCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {list.isLoading && <div style={{ color: '#64748b' }}>Loading submissions…</div>}
      {list.isError && (
        <div style={{ color: '#dc2626' }}>
          Error: {(list.error as Error)?.message ?? 'unknown'}
        </div>
      )}

      {list.data && list.data.submissions.length === 0 && (
        <div style={{ color: '#64748b', padding: '40px 0', textAlign: 'center' }}>
          No submissions in this state.
        </div>
      )}

      {list.data && list.data.submissions.length > 0 && (
        <div style={{ border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <th style={{ textAlign: 'left', padding: '10px 12px' }}>Submitted</th>
                <th style={{ textAlign: 'left', padding: '10px 12px' }}>Site</th>
                <th style={{ textAlign: 'left', padding: '10px 12px' }}>Type</th>
                <th style={{ textAlign: 'left', padding: '10px 12px' }}>Headline</th>
                <th style={{ textAlign: 'left', padding: '10px 12px' }}>Submitter</th>
                <th style={{ textAlign: 'left', padding: '10px 12px' }}>Status</th>
                <th style={{ textAlign: 'right', padding: '10px 12px' }}></th>
              </tr>
            </thead>
            <tbody>
              {list.data.submissions.map((s) => (
                <tr
                  key={s.id}
                  style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}
                  onClick={() => setOpenId(s.id)}
                >
                  <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>{fmt(s.created_at)}</td>
                  <td style={{ padding: '10px 12px' }}>
                    <div style={{ fontWeight: 600 }}>{s.marketing_name ?? s.af_number}</div>
                    <div style={{ color: '#64748b', fontSize: 12 }}>{s.af_number}</div>
                  </td>
                  <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                    {s.submission_type === 'news_events'
                      ? 'News & Events'
                      : s.submission_type === 'blog'
                        ? 'Blog'
                        : 'News + Blog'}
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    {s.status === 'published' && s.published_url ? (
                      <a
                        href={s.published_url}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        style={{ color: '#2563eb' }}
                      >
                        {rowHeadline(s)}
                      </a>
                    ) : (
                      rowHeadline(s)
                    )}
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <div>{s.submitter_name ?? s.submitter_email ?? '—'}</div>
                    {(s.on_behalf === 1 || s.on_behalf === true) && (
                      <div style={{ color: '#92400e', fontSize: 12 }}>(on behalf)</div>
                    )}
                  </td>
                  <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                    <StatusPill status={s.status} heldBy={s.held_by_name} />
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                    <div style={{ display: 'inline-flex', gap: 6, justifyContent: 'flex-end' }}>
                      {s.status === 'publish_failed' && (
                        <>
                          <RetryPublishButton
                            submissionId={s.id}
                            onDone={refreshAll}
                            variant="row"
                          />
                          <DismissFailureButton
                            submissionId={s.id}
                            onDone={refreshAll}
                            variant="row"
                          />
                        </>
                      )}
                      <button
                        type="button"
                        style={{
                          padding: '4px 10px',
                          background: '#f1f5f9',
                          border: '1px solid #cbd5e1',
                          borderRadius: 4,
                          fontSize: 13,
                          cursor: 'pointer',
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenId(s.id);
                        }}
                      >
                        Review
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openId != null && (
        <SubmissionDrawer key={openId} id={openId} onClose={() => setOpenId(null)} />
      )}
    </div>
  );
}

function SubmissionDrawer({ id, onClose }: { id: number; onClose: () => void }) {
  const queryClient = useQueryClient();
  const detail = useContentSubmissionDetailQuery(id);
  const [actionInFlight, setActionInFlight] = useState<'approve' | 'reject' | 'hold' | null>(null);
  const [rejectChoice, setRejectChoice] = useState('');
  const [rejectNote, setRejectNote] = useState('');
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [firstBlogDismissed, setFirstBlogDismissed] = useState(false);
  const [ackingFirstBlog, setAckingFirstBlog] = useState(false);
  // 'asap' = publish immediately on approval. 'schedule' = use the datetime
  // below. Only meaningful for blog submissions (news-events posts to a page).

  async function refresh() {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['content-submissions'] }),
      queryClient.invalidateQueries({ queryKey: ['content-pending-count'] }),
      queryClient.invalidateQueries({ queryKey: ['content-submission-detail', id] }),
    ]);
  }

  async function handleApprove() {
    setActionInFlight('approve');
    try {
      await contentApi.approve(id);
      showToast('Approved. Publish queued.', { variant: 'success' });
      await refresh();
    } catch (err) {
      showToast(`Approve failed: ${(err as Error).message}`, { variant: 'error' });
    } finally {
      setActionInFlight(null);
    }
  }

  async function handleHoldToggle(held: boolean) {
    setActionInFlight('hold');
    try {
      const r = held ? await contentApi.unhold(id) : await contentApi.hold(id);
      showToast(held ? 'Hold released.' : `On hold by ${r.held_by_name}.`, { variant: 'success' });
      await refresh();
    } catch (err) {
      showToast(`${held ? 'Release' : 'Hold'} failed: ${(err as Error).message}`, { variant: 'error' });
    } finally {
      setActionInFlight(null);
    }
  }

  async function handleReject() {
    if (!rejectChoice) {
      showToast('Please choose a rejection reason.', { variant: 'error' });
      return;
    }
    if (rejectChoice === 'Other' && !rejectNote.trim()) {
      showToast('Please add a note explaining the rejection.', { variant: 'error' });
      return;
    }
    const reason = rejectChoice === 'Other' ? rejectNote.trim() : rejectChoice;
    setActionInFlight('reject');
    try {
      await contentApi.reject(id, reason);
      showToast('Rejected.', { variant: 'success' });
      await refresh();
      onClose();
    } catch (err) {
      showToast(`Reject failed: ${(err as Error).message}`, { variant: 'error' });
    } finally {
      setActionInFlight(null);
    }
  }

  const sub = detail.data?.submission;

  const isBlogSubmission =
    sub?.submission_type === 'blog' || sub?.submission_type === 'both';
  const showFirstBlogNotice =
    !!sub && isBlogSubmission && !!sub.is_first_blog && !sub.blog_setup_acked && !firstBlogDismissed;

  useEffect(() => {
    if (showFirstBlogNotice) fireConfetti();
  }, [showFirstBlogNotice]);

  async function ackFirstBlog() {
    setAckingFirstBlog(true);
    try {
      await contentApi.ackBlogSetup(id);
    } catch (err) {
      showToast(`Could not save: ${(err as Error).message}`, { variant: 'error' });
    } finally {
      setAckingFirstBlog(false);
      setFirstBlogDismissed(true);
      refresh();
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.5)',
        zIndex: 1000,
        display: 'flex',
        justifyContent: 'flex-end',
      }}
    >
      {showFirstBlogNotice && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1100,
            background: 'rgba(15, 23, 42, 0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 14,
              maxWidth: 440,
              width: '100%',
              padding: '28px 26px',
              textAlign: 'center',
              boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
            }}
          >
            <div style={{ fontSize: 42, lineHeight: 1, marginBottom: 8 }}>🎉</div>
            <h3 style={{ margin: '0 0 10px', fontSize: 20, fontWeight: 800, color: '#0f172a' }}>
              This is this franchisee&apos;s first blog!
            </h3>
            <p style={{ margin: '0 0 22px', fontSize: 15, lineHeight: 1.5, color: '#334155' }}>
              Contact <strong>Jon</strong> to make sure the Blog options are filled out
              correctly.
            </p>
            <button
              type="button"
              onClick={ackFirstBlog}
              disabled={ackingFirstBlog}
              style={{
                width: '100%',
                padding: '12px 16px',
                background: '#2a5a8c',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 600,
                cursor: ackingFirstBlog ? 'default' : 'pointer',
                opacity: ackingFirstBlog ? 0.7 : 1,
              }}
            >
              {ackingFirstBlog
                ? 'Saving…'
                : "I've informed marketing that this franchisee needs their blog options filled out"}
            </button>
          </div>
        </div>
      )}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff',
          width: '100%',
          maxWidth: 720,
          height: '100%',
          overflowY: 'auto',
          padding: 24,
          boxShadow: '-4px 0 16px rgba(0,0,0,0.1)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 18 }}>Submission Review</h2>
          <button
            type="button"
            onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#64748b' }}
          >
            ×
          </button>
        </div>

        {detail.isLoading && <div>Loading…</div>}

        {sub && (
          <>
            <div style={{ background: '#f8fafc', padding: 12, borderRadius: 6, marginBottom: 16 }}>
              <div style={{ fontSize: 13, color: '#64748b' }}>
                {sub.af_number} — {sub.marketing_name ?? '(no marketing name)'}
              </div>
              {sub.site_url && (
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                  Target: <a href={sub.site_url} target="_blank" rel="noreferrer">{sub.site_url}</a>
                </div>
              )}
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                {sub.submission_type === 'news_events'
                  ? 'News & Events'
                  : sub.submission_type === 'blog'
                    ? 'Blog'
                    : 'News + Blog'}
                {sub.published_url && (
                  <>
                    :{' '}
                    <a href={sub.published_url} target="_blank" rel="noreferrer">
                      {sub.published_url}
                    </a>
                  </>
                )}
              </div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 6 }}>
                Submitted by {sub.submitter_name ?? sub.submitter_email ?? '?'}
                {sub.submitting_on_behalf_user_id ? ' (on behalf)' : ''} on {fmt(sub.created_at)}
              </div>
              <div style={{ marginTop: 8 }}>
                <StatusPill status={sub.status} heldBy={sub.held_by_name} />
              </div>
            </div>

            {sub.news_headline && (
              <section style={{ marginBottom: 24 }}>
                <h3 style={{ fontSize: 14, color: '#64748b', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>News & Events</h3>
                <h4 style={{ fontSize: 18, fontWeight: 700, margin: '4px 0 8px' }}>{sub.news_headline}</h4>
                <BodySection
                  submissionId={id}
                  field="news_content"
                  html={sub.news_content ?? ''}
                  previewHtml={sub.news_content ?? ''}
                  editable={sub.status === 'pending'}
                  onChanged={refresh}
                />
              </section>
            )}

            {sub.blog_headline && (
              <section style={{ marginBottom: 24 }}>
                <h3 style={{ fontSize: 14, color: '#64748b', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>Blog Post</h3>
                <h4 style={{ fontSize: 18, fontWeight: 700, margin: '4px 0 8px' }}>{sub.blog_headline}</h4>
                <BodySection
                  submissionId={id}
                  field="blog_content"
                  html={sub.blog_content ?? ''}
                  previewHtml={renderEmbedsForPreview(sub.blog_content ?? '')}
                  editable={sub.status === 'pending'}
                  onChanged={refresh}
                />
              </section>
            )}

            {sub.images && sub.images.length > 0 && (
              <section style={{ marginBottom: 24 }}>
                <h3 style={{ fontSize: 14, color: '#64748b', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>Images ({sub.images.length})</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                  {sub.images.map((img, i) => (
                    <a key={i} href={img.url} target="_blank" rel="noreferrer">
                      <img
                        src={img.url}
                        alt=""
                        style={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 4, border: '1px solid #e2e8f0' }}
                      />
                    </a>
                  ))}
                </div>
                <p style={{ marginTop: 10, fontSize: 13, color: sub.photo_release_confirmed ? '#15803d' : '#b45309' }}>
                  {sub.photo_release_confirmed
                    ? '✓ Submitter confirmed a photo/video release is on file for everyone shown.'
                    : '⚠ No photo/video release attestation on this submission (submitted before the checkbox existed, or not confirmed).'}
                </p>
                <p style={{ marginTop: 4, fontSize: 13, color: sub.image_rights_confirmed ? '#15803d' : '#b45309' }}>
                  {sub.image_rights_confirmed
                    ? '✓ Submitter confirmed they own or are licensed to use these photos.'
                    : '⚠ No image rights attestation on this submission (submitted before the checkbox existed, or not confirmed).'}
                </p>
              </section>
            )}

            {sub.status === 'pending' &&
              (sub.submission_type === 'blog' || sub.submission_type === 'both') && (
                <AuthorPanel submissionId={id} author={sub.blog_author} onChanged={refresh} />
              )}

            {sub.status === 'pending' && (
              <SeoMetadataPanel
                submissionId={id}
                submissionType={sub.submission_type}
                images={sub.images ?? []}
                seo={sub.seo_metadata}
                summary={sub.seo_metadata?.summary ?? null}
                showSummary={sub.seo_metadata?.showSummary ?? null}
                alts={sub.image_alts}
                onChanged={refresh}
              />
            )}

            {sub.status === 'rejected' && sub.rejection_reason && (
              <div style={{ background: '#fee2e2', padding: 12, borderRadius: 6, marginBottom: 16, fontSize: 13 }}>
                <strong>Rejection reason:</strong> {sub.rejection_reason}
              </div>
            )}

            {sub.publish_error && (
              <div style={{ background: '#fee2e2', padding: 12, borderRadius: 6, marginBottom: 16, fontSize: 13 }}>
                <strong>Publish error:</strong> {sub.publish_error}
              </div>
            )}

            {sub.status === 'publish_failed' && (
              <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 16, marginTop: 16 }}>
                <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 10px' }}>
                  This post is not live. Most publish failures are temporary
                  (a dropped connection to WP Engine, a timeout) and a retry
                  clears them.
                </p>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <RetryPublishButton submissionId={id} onDone={refresh} variant="drawer" />
                  <DismissFailureButton submissionId={id} onDone={refresh} variant="drawer" />
                </div>
              </div>
            )}

            {sub.status === 'pending' && (
              <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 16, marginTop: 16 }}>
                {!showRejectInput && sub.publish_at && (
                  <div
                    style={{
                      marginBottom: 16,
                      fontSize: 13,
                      background: '#f5f3ff',
                      border: '1px solid #ddd6fe',
                      borderRadius: 6,
                      padding: '8px 12px',
                      color: '#5b21b6',
                    }}
                  >
                    🗓️ Scheduled by the franchisee for <strong>{fmt(sub.publish_at)}</strong>. It
                    will publish on that date once approved.
                  </div>
                )}
                {!showRejectInput ? (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      type="button"
                      onClick={handleApprove}
                      disabled={actionInFlight !== null}
                      style={{
                        padding: '10px 20px',
                        background: '#16a34a',
                        color: 'white',
                        border: 'none',
                        borderRadius: 4,
                        fontWeight: 600,
                        cursor: actionInFlight ? 'not-allowed' : 'pointer',
                        opacity: actionInFlight ? 0.6 : 1,
                      }}
                    >
                      {actionInFlight === 'approve' ? 'Approving…' : 'Approve & Publish'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowRejectInput(true)}
                      disabled={actionInFlight !== null}
                      style={{
                        padding: '10px 20px',
                        background: '#fff',
                        color: '#dc2626',
                        border: '1px solid #dc2626',
                        borderRadius: 4,
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      Reject
                    </button>
                    <button
                      type="button"
                      onClick={() => handleHoldToggle(!!sub.held_by_name)}
                      disabled={actionInFlight !== null}
                      title={
                        sub.held_by_name
                          ? `On hold by ${sub.held_by_name} since ${fmt(sub.held_at)}`
                          : 'Flag this as being looked at, so the team knows to leave it'
                      }
                      style={{
                        padding: '10px 20px',
                        background: sub.held_by_name ? HOLD_COLOR + '14' : '#fff',
                        color: HOLD_COLOR,
                        border: `1px solid ${HOLD_COLOR}`,
                        borderRadius: 4,
                        fontWeight: 600,
                        cursor: actionInFlight ? 'not-allowed' : 'pointer',
                        opacity: actionInFlight ? 0.6 : 1,
                      }}
                    >
                      {actionInFlight === 'hold'
                        ? 'Saving…'
                        : sub.held_by_name
                          ? `Release hold (${sub.held_by_name})`
                          : 'Hold for Review'}
                    </button>
                  </div>
                ) : (
                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                      Reason for rejection (shown to the franchisee):
                    </label>
                    <select
                      value={rejectChoice}
                      onChange={(e) => setRejectChoice(e.target.value)}
                      style={{ width: '100%', padding: 8, border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 14, marginBottom: 8 }}
                    >
                      <option value="">Select a reason…</option>
                      {REJECT_REASONS.map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </select>
                    {rejectChoice === 'Other' && (
                      <textarea
                        value={rejectNote}
                        onChange={(e) => setRejectNote(e.target.value)}
                        rows={3}
                        placeholder="Explain why (required when 'Other' is selected)"
                        style={{ width: '100%', padding: 8, border: '1px solid #cbd5e1', borderRadius: 4, fontFamily: 'inherit', fontSize: 14, marginBottom: 8 }}
                      />
                    )}
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        type="button"
                        onClick={handleReject}
                        disabled={actionInFlight !== null}
                        style={{ padding: '8px 16px', background: '#dc2626', color: 'white', border: 'none', borderRadius: 4, fontWeight: 600, cursor: 'pointer' }}
                      >
                        {actionInFlight === 'reject' ? 'Rejecting…' : 'Confirm Reject'}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setShowRejectInput(false); setRejectChoice(''); setRejectNote(''); }}
                        style={{ padding: '8px 16px', background: '#fff', border: '1px solid #cbd5e1', borderRadius: 4, cursor: 'pointer' }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// BodySection — the body preview plus the marketer's tools for it: an Edit
// button (beside "Check spelling & grammar") that swaps the preview for a
// textarea, and Save which writes the text back to the submission so Approve &
// Publish ships the edited copy.
//
// Editing is visual, in the SAME Quill editor (and with the same embed blot)
// the franchisee wrote the post in — so the marketer sees formatted text, not
// markup, and an embed survives a round trip instead of being stripped out.
//
// Note this edits the RAW stored HTML, never the rendered preview: the preview
// runs renderEmbedsForPreview(), which rewrites embed blocks into display
// cards and drops their data-embed-url.
// ---------------------------------------------------------------------------
function BodySection(props: {
  submissionId: number;
  field: 'news_content' | 'blog_content';
  html: string;
  previewHtml: string;
  editable: boolean;
  onChanged: () => Promise<void> | void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(props.html);
  const [saving, setSaving] = useState(false);
  // Blog bodies can carry embeds, so the blog editor gets the Embed button.
  const isBlog = props.field === 'blog_content';

  const boxStyle = {
    border: '1px solid #e2e8f0',
    padding: 12,
    borderRadius: 6,
    fontSize: 14,
    lineHeight: 1.5,
  } as const;

  function startEditing() {
    setDraft(props.html);
    setEditing(true);
  }

  async function save() {
    if (isQuillBlank(draft)) {
      showToast('Body cannot be empty.', { variant: 'error' });
      return;
    }
    setSaving(true);
    try {
      // Same heading hygiene the submission form applies on the way in.
      await contentApi.saveBody(props.submissionId, props.field, normalizeHeadings(draft));
      showToast('Changes saved.', { variant: 'success' });
      await props.onChanged();
      setEditing(false);
    } catch (err) {
      showToast(`Save failed: ${(err as Error).message}`, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  }

  if (!editing) {
    return (
      <>
        <div
          style={boxStyle}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: props.previewHtml }}
        />
        {props.editable && (
          <SpellCheckPanel
            submissionId={props.submissionId}
            field={props.field}
            onApplied={props.onChanged}
            extraButton={
              <button
                type="button"
                onClick={startEditing}
                style={{
                  padding: '6px 12px',
                  background: '#fff',
                  border: '1px solid #cbd5e1',
                  borderRadius: 4,
                  fontSize: 13,
                  cursor: 'pointer',
                  color: '#475569',
                }}
              >
                Edit
              </button>
            }
          />
        )}
      </>
    );
  }

  return (
    <>
      <div className="amada-body-editor">
        <ReactQuill
          theme="snow"
          value={draft}
          onChange={setDraft}
          modules={isBlog ? QUILL_MODULES_BLOG : QUILL_MODULES}
          formats={isBlog ? QUILL_FORMATS_BLOG : QUILL_FORMATS}
        />
      </div>
      <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          style={{
            padding: '6px 12px',
            background: '#16a34a',
            color: 'white',
            border: 'none',
            borderRadius: 4,
            fontSize: 13,
            fontWeight: 600,
            cursor: saving ? 'not-allowed' : 'pointer',
            opacity: saving ? 0.6 : 1,
          }}
        >
          {saving ? 'Saving…' : 'Save changes'}
        </button>
        <button
          type="button"
          onClick={() => setEditing(false)}
          disabled={saving}
          style={{
            padding: '6px 12px',
            background: '#fff',
            border: '1px solid #cbd5e1',
            borderRadius: 4,
            fontSize: 13,
            cursor: 'pointer',
            color: '#475569',
          }}
        >
          Cancel
        </button>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// SpellCheckPanel — inline panel rendered under each body section so the
// marketer can run a conservative Haiku spell/grammar pass before approving.
// Result is cached server-side by body hash; "Apply" writes the corrected
// HTML back to the submission's body column.
// ---------------------------------------------------------------------------
function SpellCheckPanel(props: {
  submissionId: number;
  field: 'news_content' | 'blog_content';
  onApplied: () => Promise<void> | void;
  // Rendered beside the "Check spelling & grammar" button (the Edit button).
  // Only shown in the idle state — starting an edit mid-review would leave the
  // cached suggestions pointing at text that no longer exists.
  extraButton?: ReactNode;
}) {
  const [state, setState] = useState<
    | { kind: 'idle' }
    | { kind: 'loading' }
    | { kind: 'result'; result: import('../api/content').SpellCheckResult }
    | { kind: 'applying'; result: import('../api/content').SpellCheckResult }
    | { kind: 'error'; message: string }
  >({ kind: 'idle' });

  async function runCheck() {
    setState({ kind: 'loading' });
    try {
      const result = await contentApi.spellcheckSubmission(props.submissionId, props.field);
      setState({ kind: 'result', result });
    } catch (err) {
      setState({ kind: 'error', message: (err as Error).message });
    }
  }

  async function applyAll() {
    if (state.kind !== 'result') return;
    setState({ kind: 'applying', result: state.result });
    try {
      const r = await contentApi.applySpellcheck(
        props.submissionId,
        props.field,
        state.result.bodyHash,
      );
      showToast(`Applied ${r.appliedFixes} correction${r.appliedFixes === 1 ? '' : 's'}.`, {
        variant: 'success',
      });
      await props.onApplied();
      setState({ kind: 'idle' });
    } catch (err) {
      const msg = (err as Error).message;
      showToast(`Apply failed: ${msg}`, { variant: 'error' });
      setState({ kind: 'result', result: state.result });
    }
  }

  if (state.kind === 'idle') {
    return (
      <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
        <button
          type="button"
          onClick={runCheck}
          style={{
            padding: '6px 12px',
            background: '#fff',
            border: '1px solid #cbd5e1',
            borderRadius: 4,
            fontSize: 13,
            cursor: 'pointer',
            color: '#475569',
          }}
        >
          Check spelling &amp; grammar
        </button>
        {props.extraButton}
      </div>
    );
  }

  if (state.kind === 'loading') {
    return (
      <div style={{ marginTop: 8, fontSize: 13, color: '#64748b' }}>Checking…</div>
    );
  }

  if (state.kind === 'error') {
    return (
      <div style={{ marginTop: 8, fontSize: 13, color: '#dc2626' }}>
        Check failed: {state.message}{' '}
        <button
          type="button"
          onClick={() => setState({ kind: 'idle' })}
          style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', padding: 0, fontSize: 13 }}
        >
          Try again
        </button>
      </div>
    );
  }

  const result = state.result;
  const applying = state.kind === 'applying';

  if (!result.hasChanges) {
    return (
      <div
        style={{
          marginTop: 8,
          fontSize: 13,
          color: '#16a34a',
          background: '#f0fdf4',
          border: '1px solid #bbf7d0',
          padding: '6px 10px',
          borderRadius: 4,
        }}
      >
        ✓ No spelling or grammar issues found
        {result.cached && <span style={{ color: '#64748b', marginLeft: 6 }}>(cached)</span>}
      </div>
    );
  }

  return (
    <div
      style={{
        marginTop: 8,
        border: '1px solid #fde68a',
        background: '#fffbeb',
        padding: 12,
        borderRadius: 6,
        fontSize: 13,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <strong style={{ color: '#92400e' }}>
          {result.fixes.length} suggestion{result.fixes.length === 1 ? '' : 's'}
          {result.cached && <span style={{ color: '#64748b', fontWeight: 400, marginLeft: 6 }}>(cached)</span>}
        </strong>
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            type="button"
            disabled={applying}
            onClick={applyAll}
            style={{
              padding: '4px 10px',
              background: '#16a34a',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              fontSize: 12,
              fontWeight: 600,
              cursor: applying ? 'not-allowed' : 'pointer',
              opacity: applying ? 0.6 : 1,
            }}
          >
            {applying ? 'Applying…' : 'Apply all corrections'}
          </button>
          <button
            type="button"
            disabled={applying}
            onClick={() => setState({ kind: 'idle' })}
            style={{
              padding: '4px 10px',
              background: '#fff',
              border: '1px solid #cbd5e1',
              borderRadius: 4,
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            Dismiss
          </button>
        </div>
      </div>
      <ul style={{ margin: 0, paddingLeft: 18 }}>
        {result.fixes.map((f, i) => (
          <li key={i} style={{ marginBottom: 6 }}>
            <span style={{ background: '#fee2e2', textDecoration: 'line-through', padding: '0 3px' }}>
              {f.original}
            </span>{' '}
            →{' '}
            <span style={{ background: '#dcfce7', padding: '0 3px' }}>{f.corrected}</span>
            <span style={{ color: '#64748b', marginLeft: 6, fontSize: 12 }}>({f.reason})</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SeoMetadataPanel — editable Yoast SEO fields (blog only) + per-image alt
// text. Marketer hits "Generate" to populate from Haiku; resulting fields
// become editable inputs. Saved values are what the publish script uses.
// ---------------------------------------------------------------------------
type Yoast = import('../api/content').YoastFields;
type ImgMeta = import('../api/content').ImageMeta;

// The server (server/src/services/seoMetadata.ts) silently substitutes a
// fallback when the AI alt-text call fails (e.g. Anthropic API out of credits):
//   alt: `Image from "<headline>"`  -- or  "Article image"
// Detecting that signature lets us warn the marketer that what they see is
// placeholder text, not real generated alt text. Keep in sync with that file.
const FALLBACK_ALT_RE = /^Image from "|^Article image$/;
function altsLookLikeFallback(alts: ImgMeta[]): boolean {
  return alts.some((a) => FALLBACK_ALT_RE.test((a.alt ?? '').trim()));
}

// The server (seoMetadata.ts) substitutes a recognizable placeholder when the
// AI article-summary call fails (e.g. Anthropic API out of credits). Detecting
// that signature lets us warn the marketer here, and the publish script flips
// the WordPress "show summary" toggle OFF so it never appears publicly. Keep
// this in sync with generateArticleSummary's fallback + the publish script.
const FALLBACK_SUMMARY_RE = /^Summary of "|^Article summary unavailable\b/;
function summaryLooksLikeFallback(summary: string | null | undefined): boolean {
  const s = (summary ?? '').trim();
  return s !== '' && FALLBACK_SUMMARY_RE.test(s);
}

// ---------------------------------------------------------------------------
// ConfirmModal — lightweight yes/no overlay used to gate the author edit.
// ---------------------------------------------------------------------------
function ConfirmModal(props: {
  message: string;
  confirmLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div
      onClick={props.onCancel}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 8, padding: 24, maxWidth: 420, boxShadow: '0 10px 40px rgba(0,0,0,0.25)' }}
      >
        <div style={{ fontSize: 15, color: '#0f172a', marginBottom: 20 }}>{props.message}</div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={props.onCancel}
            style={{ padding: '6px 14px', background: '#fff', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 13, cursor: 'pointer' }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={props.onConfirm}
            style={{ padding: '6px 14px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 4, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
          >
            {props.confirmLabel ?? 'Yes, edit'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AuthorPanel — view/edit the franchisee-supplied blog author. Editing is
// gated behind a confirm dialog ("Are you sure you want to edit the author?").
// ---------------------------------------------------------------------------
function AuthorPanel(props: {
  submissionId: number;
  author: string | null;
  onChanged: () => Promise<void> | void;
}) {
  const [author, setAuthor] = useState(props.author ?? '');
  const [editing, setEditing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    try {
      await contentApi.saveAuthor(props.submissionId, author.trim());
      showToast('Author saved.', { variant: 'success' });
      setEditing(false);
      await props.onChanged();
    } catch (err) {
      showToast(`Save failed: ${(err as Error).message}`, { variant: 'error' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section style={{ marginBottom: 24 }}>
      <h3 style={{ fontSize: 14, color: '#64748b', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Author
      </h3>
      {!editing ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 14, color: author.trim() ? '#0f172a' : '#94a3b8' }}>
            {author.trim() || '(no author provided)'}
          </span>
          <button
            type="button"
            onClick={() => setConfirmOpen(true)}
            style={{ padding: '4px 12px', background: '#fff', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 13, cursor: 'pointer' }}
          >
            Edit
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            type="text"
            value={author}
            onChange={(e) => setAuthor(e.target.value.slice(0, 255))}
            placeholder="Author name"
            style={{ ...seoInputStyle, maxWidth: 320 }}
          />
          <button
            type="button"
            onClick={save}
            disabled={busy}
            style={{ padding: '6px 14px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 4, fontSize: 13, fontWeight: 600, cursor: busy ? 'not-allowed' : 'pointer' }}
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            onClick={() => {
              setAuthor(props.author ?? '');
              setEditing(false);
            }}
            style={{ padding: '6px 14px', background: '#fff', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 13, cursor: 'pointer' }}
          >
            Cancel
          </button>
        </div>
      )}
      {confirmOpen && (
        <ConfirmModal
          message="Are you sure you want to edit the author?"
          onConfirm={() => {
            setConfirmOpen(false);
            setEditing(true);
          }}
          onCancel={() => setConfirmOpen(false)}
        />
      )}
    </section>
  );
}

function SeoMetadataPanel(props: {
  submissionId: number;
  submissionType: 'news_events' | 'blog' | 'both';
  images: Array<{ url: string; name?: string }>;
  seo: Yoast | null;
  summary: string | null;
  showSummary: boolean | null;
  alts: ImgMeta[] | null;
  onChanged: () => Promise<void> | void;
}) {
  const isBlog = props.submissionType === 'blog' || props.submissionType === 'both';
  const hasGenerated = props.seo != null || props.alts != null;

  // Local editable state mirrors what the server has; seeded from props.
  const [seo, setSeo] = useState<Yoast | null>(props.seo);
  const [alts, setAlts] = useState<ImgMeta[]>(
    props.alts ?? props.images.map(() => ({ alt: '', title: '' })),
  );
  const [summary, setSummary] = useState<string>(props.summary ?? '');
  const [showSummary, setShowSummary] = useState<boolean>(
    props.showSummary ?? !summaryLooksLikeFallback(props.summary),
  );
  const [busy, setBusy] = useState<'idle' | 'generating' | 'saving'>('idle');
  const [dirty, setDirty] = useState(false);

  async function handleGenerate() {
    setBusy('generating');
    try {
      const r = await contentApi.generateSeoMetadata(props.submissionId);
      setSeo(r.seo);
      setAlts(r.alts.length ? r.alts : props.images.map(() => ({ alt: '', title: '' })));
      setSummary(r.summary ?? '');
      setShowSummary(r.showSummary ?? !summaryLooksLikeFallback(r.summary));
      setDirty(false);
      const fellBack = altsLookLikeFallback(r.alts) || summaryLooksLikeFallback(r.summary);
      showToast(
        fellBack
          ? 'Generation failed - placeholder text inserted (see warning below).'
          : 'Generated.',
        { variant: fellBack ? 'error' : 'success' },
      );
      await props.onChanged();
    } catch (err) {
      showToast(`Generate failed: ${(err as Error).message}`, { variant: 'error' });
    } finally {
      setBusy('idle');
    }
  }

  async function handleSave() {
    setBusy('saving');
    try {
      await contentApi.saveSeoMetadata(props.submissionId, {
        seo: isBlog && seo ? seo : undefined,
        summary: isBlog ? summary : undefined,
        showSummary: isBlog ? showSummary : undefined,
        alts: alts.length ? alts : undefined,
      });
      showToast('Saved.', { variant: 'success' });
      setDirty(false);
      await props.onChanged();
    } catch (err) {
      showToast(`Save failed: ${(err as Error).message}`, { variant: 'error' });
    } finally {
      setBusy('idle');
    }
  }

  return (
    <section style={{ marginBottom: 24 }}>
      <h3 style={{ fontSize: 14, color: '#64748b', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        SEO Metadata, Alt Text, and Article Summary
      </h3>

      {!hasGenerated ? (
        <div
          style={{
            border: '1px dashed #cbd5e1',
            padding: 14,
            borderRadius: 6,
            fontSize: 13,
            color: '#475569',
          }}
        >
          <div style={{ marginBottom: 8 }}>
            {isBlog
              ? 'Generate Yoast SEO fields and per-image alt text so you can review and edit before approving.'
              : 'Generate per-image alt text so you can review and edit before approving.'}
          </div>
          <button
            type="button"
            onClick={handleGenerate}
            disabled={busy !== 'idle'}
            style={{
              padding: '6px 12px',
              background: '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              fontSize: 13,
              fontWeight: 600,
              cursor: busy === 'idle' ? 'pointer' : 'not-allowed',
              opacity: busy === 'idle' ? 1 : 0.6,
            }}
          >
            {busy === 'generating' ? 'Generating…' : 'Generate SEO & Alt Text'}
          </button>
        </div>
      ) : (
        <div
          style={{
            border: '1px solid #e2e8f0',
            padding: 14,
            borderRadius: 6,
            fontSize: 13,
          }}
        >
          {(altsLookLikeFallback(alts) || summaryLooksLikeFallback(summary)) && (
            <div
              style={{
                background: '#fef2f2',
                border: '1px solid #fca5a5',
                borderRadius: 6,
                padding: '10px 12px',
                marginBottom: 16,
                fontSize: 13,
                color: '#991b1b',
              }}
            >
              ⚠️ <strong>AI generation failed - this is placeholder text.</strong> The
              alt text and/or article summary below (e.g. <code>Image from "..."</code> or{' '}
              <code>Summary of "..."</code>) is an automatic fallback, not real AI output -
              usually it means the Anthropic API is unavailable or out of credits. Restore API
              access, then click <strong>Regenerate</strong>. Don't approve with this
              placeholder text; published blog posts will hide the summary box automatically.
            </div>
          )}
          {isBlog && seo && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 600, marginBottom: 8, color: '#0f172a' }}>Yoast SEO (blog post)</div>
              <Label text="SEO Title (≤ 60 chars)">
                <input
                  type="text"
                  value={seo.seoTitle}
                  onChange={(e) => {
                    setSeo({ ...seo, seoTitle: e.target.value });
                    setDirty(true);
                  }}
                  maxLength={70}
                  style={seoInputStyle}
                />
                <CharHint current={seo.seoTitle.length} max={60} />
              </Label>
              <Label text="Meta Description (140–160 chars)">
                <textarea
                  value={seo.metaDesc}
                  onChange={(e) => {
                    setSeo({ ...seo, metaDesc: e.target.value });
                    setDirty(true);
                  }}
                  rows={3}
                  maxLength={200}
                  style={{ ...seoInputStyle, fontFamily: 'inherit', resize: 'vertical' }}
                />
                <CharHint current={seo.metaDesc.length} min={140} max={160} />
              </Label>
              <Label text="URL Slug">
                <input
                  type="text"
                  value={seo.slug}
                  onChange={(e) => {
                    setSeo({ ...seo, slug: e.target.value });
                    setDirty(true);
                  }}
                  style={{ ...seoInputStyle, fontFamily: 'monospace' }}
                />
              </Label>
            </div>
          )}

          {isBlog && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 600, marginBottom: 8, color: '#0f172a' }}>
                Article Summary (blog post)
              </div>
              <Label text="Summary — shown in a box at the top of the published post (2–3 sentences)">
                <textarea
                  value={summary}
                  onChange={(e) => {
                    setSummary(e.target.value.slice(0, 800));
                    setDirty(true);
                  }}
                  rows={3}
                  maxLength={800}
                  style={{ ...seoInputStyle, fontFamily: 'inherit', resize: 'vertical' }}
                />
                <CharHint current={summary.length} max={600} />
              </Label>
              <label
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 13,
                  color: '#0f172a',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={showSummary}
                  onChange={(e) => {
                    setShowSummary(e.target.checked);
                    setDirty(true);
                  }}
                />
                Show the summary box on the published blog post
              </label>
            </div>
          )}

          {alts.length > 0 && (
            <div>
              <div style={{ fontWeight: 600, marginBottom: 8, color: '#0f172a' }}>
                Image Alt Text ({alts.length})
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 12, alignItems: 'start' }}>
                {props.images.slice(0, alts.length).map((img, i) => (
                  <Fragment key={i}>
                    <a href={img.url} target="_blank" rel="noreferrer">
                      <img
                        src={img.url}
                        alt=""
                        style={{ width: 120, height: 90, objectFit: 'cover', borderRadius: 4, border: '1px solid #e2e8f0' }}
                      />
                    </a>
                    <div>
                      <Label text="Alt text">
                        <textarea
                          value={alts[i]?.alt ?? ''}
                          onChange={(e) => {
                            const next = alts.slice();
                            next[i] = { ...next[i], alt: e.target.value };
                            setAlts(next);
                            setDirty(true);
                          }}
                          rows={2}
                          style={{ ...seoInputStyle, fontFamily: 'inherit', resize: 'vertical' }}
                        />
                      </Label>
                      <Label text="Media Library Title">
                        <input
                          type="text"
                          value={alts[i]?.title ?? ''}
                          onChange={(e) => {
                            const next = alts.slice();
                            next[i] = { ...next[i], title: e.target.value };
                            setAlts(next);
                            setDirty(true);
                          }}
                          style={seoInputStyle}
                        />
                      </Label>
                    </div>
                  </Fragment>
                ))}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button
              type="button"
              onClick={handleSave}
              disabled={busy !== 'idle' || !dirty}
              style={{
                padding: '6px 14px',
                background: dirty ? '#16a34a' : '#94a3b8',
                color: 'white',
                border: 'none',
                borderRadius: 4,
                fontSize: 13,
                fontWeight: 600,
                cursor: dirty && busy === 'idle' ? 'pointer' : 'not-allowed',
              }}
            >
              {busy === 'saving' ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}
            </button>
            <button
              type="button"
              onClick={handleGenerate}
              disabled={busy !== 'idle'}
              style={{
                padding: '6px 14px',
                background: '#fff',
                color: '#475569',
                border: '1px solid #cbd5e1',
                borderRadius: 4,
                fontSize: 13,
                cursor: busy === 'idle' ? 'pointer' : 'not-allowed',
              }}
            >
              {busy === 'generating' ? 'Regenerating…' : 'Regenerate'}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

const seoInputStyle: React.CSSProperties = {
  width: '100%',
  padding: '6px 8px',
  border: '1px solid #cbd5e1',
  borderRadius: 4,
  fontSize: 13,
  boxSizing: 'border-box',
};

function Label({ text, children }: { text: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'block', marginBottom: 10 }}>
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>{text}</div>
      {children}
    </label>
  );
}

function CharHint({ current, min, max }: { current: number; min?: number; max: number }) {
  const tooShort = min != null && current < min;
  const tooLong = current > max;
  const color = tooShort || tooLong ? '#dc2626' : '#94a3b8';
  return (
    <div style={{ fontSize: 11, color, marginTop: 2 }}>
      {current}{min ? `/${min}-${max}` : `/${max}`} chars
    </div>
  );
}
